import React, { useState, useEffect } from 'react';
import { getPageImageUrl } from '../data/pageData.jsx';

/**
 * Scopes all CSS selectors under `scope` to beat the platform's preflight
 * specificity (which wraps Tailwind under .app-id h1 etc. → 0,1,1).
 * Rules like :root, *, html, ::selection, @keyframes internals are left global.
 */
function scopeCSS(css, scope) {
  const skipPattern = /^(\*|html|:root|::|from$|to$|\d)/;
  // [^{}@]+ matches selector text that precedes a {, skipping @-rule openers.
  // We also check if the char before the match is '@' — if so, this is the
  // tail of an @-rule (e.g. "keyframes fadeUp" from "@keyframes fadeUp {")
  // and must be left untouched.
  return css.replace(/([^{}@]+)\{/g, (match, rawSelector, offset) => {
    if (offset > 0 && css[offset - 1] === '@') return match;
    const sel = rawSelector.trim();
    if (!sel || skipPattern.test(sel)) return match;
    const scoped = sel.split(',').map(s => {
      const t = s.trim();
      if (!t || skipPattern.test(t) || t.startsWith(scope)) return t;
      return `${scope} ${t}`;
    }).join(', ');
    return `\n${scoped} {`;
  });
}
const EF2_CSS = `
:root {
  --bone: #f5f1e8; --bone-2: #ece7d8; --bone-3: #fbf8ef;
  --paper: #fdfaf2; --ink: #15120d; --ink-2: #2b261c;
  --ink-3: #4a4336; --ink-4: #8a8170; --rule: #d4cdb5; --rule-2: #a89f82;
  --ox: #7a1f1f; --ox-2: #9a2a22; --ox-3: #b53a30;
  --saffron: #d97f2a; --green: #1a5c3a;
  --font-serif: 'Playfair Display', Georgia, 'Times New Roman', serif;
  --font-sans: 'Inter Tight', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', ui-monospace, monospace;
  --ease: cubic-bezier(0.2, 0.7, 0.2, 1);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --container: 1240px;
}
* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
.ef2-root { font-family: var(--font-sans); color: var(--ink); background: var(--bone); -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; line-height: 1.5; overflow-x: hidden; position: relative; }
::selection { background: var(--ox); color: var(--paper); }
.ef2-grain { position: fixed; inset: 0; pointer-events: none; z-index: 0; opacity: 0.3; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='3'/><feColorMatrix values='0 0 0 0 0.2 0 0 0 0 0.17 0 0 0 0 0.13 0 0 0 0.05 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); }
.wrap { max-width: var(--container); margin: 0 auto; padding: 0 32px; position: relative; z-index: 1; }
section[id] { scroll-margin-top: 112px; }
.serif { font-family: var(--font-serif); }
.sans  { font-family: var(--font-sans); }
.mono  { font-family: var(--font-mono); }
.eyebrow { font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.rule-thin { height: 1px; background: var(--rule); }
.nav { position: sticky; top: 0; z-index: 50; background: color-mix(in oklab, var(--bone) 94%, transparent); backdrop-filter: blur(14px) saturate(1.2); -webkit-backdrop-filter: blur(14px) saturate(1.2); border-bottom: 1px solid var(--rule); }
.nav-inner { max-width: var(--container); margin: 0 auto; padding: 18px 32px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
.nav-mobile { display: none; border-top: 1px solid var(--rule); overflow-x: auto; white-space: nowrap; scrollbar-width: none; background: color-mix(in oklab, var(--bone) 92%, transparent); }
.nav-mobile::-webkit-scrollbar { display: none; }
.nav-mobile .wrap { display: flex; gap: 12px; padding-top: 10px; padding-bottom: 12px; }
.nav-mobile a { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; text-decoration: none; color: var(--ink-3); padding: 8px 12px; border: 1px solid var(--rule); border-radius: 999px; background: var(--paper); white-space: nowrap; }
.brand { font-family: var(--font-serif); font-size: 22px; white-space: nowrap; font-weight: 600; letter-spacing: -0.015em; color: var(--ink); text-decoration: none; }
.brand em { font-style: italic; font-weight: 500; color: var(--ox); }
.nav-links { display: flex; gap: 32px; align-items: center; font-family: var(--font-sans); font-size: 14px; font-weight: 500; color: var(--ink-2); }
.nav-links a { color: inherit; text-decoration: none; transition: color 150ms; }
.nav-links a:hover { color: var(--ox); }
.btn { font-family: var(--font-sans); font-size: 14px; font-weight: 600; padding: 13px 22px; border: 1px solid transparent; cursor: pointer; transition: all 200ms var(--ease); text-decoration: none; display: inline-flex; align-items: center; gap: 10px; white-space: nowrap; border-radius: 999px; background: transparent; color: var(--ink); letter-spacing: -0.005em; }
.btn-primary { color: var(--paper); background: var(--ink); border-color: var(--ink); }
.btn-primary:hover { background: var(--ox); border-color: var(--ox); }
.btn-ghost { border-color: var(--ink); color: var(--ink); }
.btn-ghost:hover { background: var(--ink); color: var(--paper); }
.btn-ox { color: var(--paper); background: var(--ox); border-color: var(--ox); }
.btn-ox:hover { background: var(--ox-2); }
.btn-sm { padding: 9px 15px; font-size: 12.5px; }
.btn-lg { padding: 17px 28px; font-size: 15px; }
.btn .arrow { display: inline-block; transition: transform 200ms var(--ease); }
.btn:hover .arrow { transform: translateX(3px); }
.hero { padding: 80px 0 96px; position: relative; }
.hero-grid { display: grid; grid-template-columns: 1.35fr 1fr; gap: 72px; align-items: start; }
.hero-tag { display: inline-flex; align-items: center; gap: 14px; font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; padding: 8px 14px; border: 1px solid var(--rule); border-radius: 999px; background: var(--bone-3); margin-bottom: 38px; }
.hero-tag .pulse { width: 7px; height: 7px; border-radius: 999px; background: var(--ox); box-shadow: 0 0 0 4px color-mix(in oklab, var(--ox) 22%, transparent); animation: pulse 2.2s var(--ease) infinite; }
@keyframes pulse { 0%, 100% { box-shadow: 0 0 0 4px color-mix(in oklab, var(--ox) 22%, transparent); } 50% { box-shadow: 0 0 0 7px color-mix(in oklab, var(--ox) 0%, transparent); } }
.hero-title { font-family: var(--font-serif); font-weight: 500; font-size: clamp(64px, 9vw, 148px); line-height: 0.92; letter-spacing: -0.035em; color: var(--ink); margin: 0; text-wrap: balance; }
.hero-title .amp { font-family: var(--font-serif); font-style: italic; font-weight: 400; color: var(--ox); padding: 0 0.02em; }
.hero-title .italic { font-style: italic; font-weight: 400; display: block; color: var(--ink-2); }
.hero-stat-row { display: grid; grid-template-columns: repeat(3, auto); gap: 40px; margin: 52px 0 0; padding: 28px 0 0; border-top: 1px solid var(--rule); }
.hero-stat .n { font-family: var(--font-serif); font-weight: 500; font-size: 54px; line-height: 1; letter-spacing: -0.03em; color: var(--ink); }
.hero-stat .n em { font-style: italic; color: var(--ox); font-weight: 400; }
.hero-stat .l { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); margin-top: 8px; font-weight: 500; max-width: 18ch; line-height: 1.5; }
.hero-sub { font-family: var(--font-serif); font-style: italic; font-weight: 400; font-size: clamp(21px, 2.1vw, 28px); line-height: 1.35; color: var(--ink-2); margin: 38px 0 0; max-width: 30ch; text-wrap: balance; }
.hero-sub b { font-style: normal; font-weight: 600; color: var(--ink); }
.hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; margin: 36px 0 0; }
.hero-ctas .meta { font-family: var(--font-sans); font-size: 13.5px; color: var(--ink-3); margin-left: 6px; }
.hero-ctas .meta b { color: var(--ink); font-weight: 600; }
.hero-right { position: relative; align-self: stretch; display: flex; align-items: flex-start; justify-content: center; }
.hero-right::before { content: ''; position: absolute; inset: 32px 36px 8% 8%; background: radial-gradient(circle at 35% 30%, rgba(122,31,31,0.14), transparent 38%), radial-gradient(circle at 70% 70%, rgba(217,127,42,0.14), transparent 34%), radial-gradient(circle at 58% 48%, rgba(21,18,13,0.09), transparent 52%); filter: blur(18px); pointer-events: none; }
.cover-plate { position: relative; width: 100%; max-width: 460px; z-index: 1; }
.cover-img { width: 100%; aspect-ratio: 567 / 800; display: block; object-fit: cover; border-radius: 2px; box-shadow: 0 1px 0 rgba(255,255,255,0.4) inset, 0 30px 60px -18px rgba(21,18,13,0.35), 0 12px 30px -12px rgba(21,18,13,0.25); }
.plate-corner { position: absolute; font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.plate-corner.tl { top: -24px; left: 0; }
.plate-corner.tr { top: -24px; right: 0; }
.plate-corner.bl { bottom: -24px; left: 0; }
.plate-corner.br { bottom: -24px; right: 0; color: var(--ox); }
.plate-corner .accent { color: var(--ox); }
.plate-mark { position: absolute; width: 18px; height: 18px; pointer-events: none; }
.plate-mark::before, .plate-mark::after { content: ''; position: absolute; background: var(--ink-3); }
.plate-mark::before { left: 50%; top: 0; bottom: 0; width: 1px; transform: translateX(-50%); }
.plate-mark::after  { top: 50%; left: 0; right: 0; height: 1px; transform: translateY(-50%); }
.plate-mark.tl { top: -10px; left: -10px; }
.plate-mark.tr { top: -10px; right: -10px; }
.plate-mark.bl { bottom: -10px; left: -10px; }
.plate-mark.br { bottom: -10px; right: -10px; }
.ticker { position: relative; overflow: hidden; padding: 18px 0; background: var(--ink); color: var(--paper); border-top: 1px solid var(--ink); border-bottom: 1px solid var(--ink); }
.ticker-track { display: flex; width: max-content; animation: ticker 80s linear infinite; font-family: var(--font-serif); font-style: italic; font-weight: 500; font-size: 28px; letter-spacing: -0.01em; color: var(--paper); white-space: nowrap; }
.ticker-track .dot  { color: var(--ox-3); margin: 0 28px; font-style: normal; }
.ticker-track .year { color: var(--ox-3); margin-right: 10px; font-family: var(--font-mono); font-style: normal; font-size: 20px; font-weight: 500; }
.ticker-track span { display: inline-block; }
@keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.ticker:hover .ticker-track { animation-play-state: paused; }
.forces { padding: 140px 0; }
.forces-head { display: grid; grid-template-columns: 1.1fr 1fr; gap: 72px; align-items: end; margin-bottom: 72px; }
.forces-head h2 { font-family: var(--font-serif); font-weight: 500; font-size: clamp(48px, 6.4vw, 92px); line-height: 0.96; letter-spacing: -0.03em; color: var(--ink); margin: 14px 0 0; text-wrap: balance; }
.forces-head h2 em { font-style: italic; color: var(--ox); }
.forces-head p { font-family: var(--font-serif); font-size: 19px; line-height: 1.55; color: var(--ink-2); margin: 0; max-width: 44ch; font-style: italic; }
.force-tabs { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0; border-top: 1px solid var(--ink); border-bottom: 1px solid var(--rule); margin-bottom: 0; }
.force-tab { appearance: none; background: transparent; border: none; border-right: 1px solid var(--rule); padding: 28px 24px 26px; text-align: left; cursor: pointer; font-family: var(--font-sans); color: var(--ink-3); display: flex; flex-direction: column; gap: 10px; transition: background 180ms, color 180ms; position: relative; }
.force-tab:last-child { border-right: none; }
.force-tab:hover { background: var(--bone-3); color: var(--ink); }
.force-tab .tnum { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-4); font-weight: 500; }
.force-tab .tname { font-family: var(--font-serif); font-size: 26px; font-weight: 500; line-height: 1.05; letter-spacing: -0.015em; color: inherit; }
.force-tab .tname em { font-style: italic; }
.force-tab .tspan { font-family: var(--font-mono); font-size: 11.5px; color: var(--ink-4); font-weight: 500; }
.force-tab.active { background: var(--ink); color: var(--paper); border-right-color: var(--ink); }
.force-tab.active .tnum, .force-tab.active .tspan { color: var(--bone-2); }
.force-tab.active::after { content: ''; position: absolute; top: -1px; left: 0; right: 0; height: 3px; }
.force-tab[data-f="s1"].active::after { background: var(--saffron); }
.force-tab[data-f="s2"].active::after { background: var(--green); }
.force-panel { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 0; min-height: 560px; border-left: 1px solid var(--rule); border-right: 1px solid var(--rule); border-bottom: 1px solid var(--ink); }
.force-copy { padding: 56px 48px; border-right: 1px solid var(--rule); background: var(--bone-3); position: relative; }
.force-copy .fnum { font-family: var(--font-serif); font-style: italic; font-weight: 400; font-size: 180px; line-height: 0.85; color: var(--ink); opacity: 0.08; position: absolute; top: 20px; right: 36px; letter-spacing: -0.04em; }
.force-copy .fkind { font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ox); font-weight: 500; margin-bottom: 14px; }
.force-copy h3 { font-family: var(--font-serif); font-weight: 500; font-size: clamp(34px, 3.6vw, 48px); line-height: 1.02; letter-spacing: -0.02em; color: var(--ink); margin: 0 0 22px; text-wrap: balance; }
.force-copy h3 em { font-style: italic; color: var(--ox); }
.force-copy .flede { font-family: var(--font-serif); font-size: 19px; line-height: 1.55; color: var(--ink-2); margin: 0 0 32px; max-width: 42ch; }
.force-copy .fmeta { display: grid; grid-template-columns: repeat(2, auto); gap: 16px 36px; padding-top: 24px; border-top: 1px solid var(--rule); max-width: 420px; }
.force-copy .fmeta .mlbl { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.force-copy .fmeta .mval { font-family: var(--font-serif); font-size: 18px; color: var(--ink); font-weight: 500; margin-top: 2px; }
.force-copy .fmeta .mval em { font-style: italic; color: var(--ox); }
.force-moments { padding: 40px 40px; display: flex; flex-direction: column; gap: 0; background: var(--paper); }
.force-moments .mhead { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ink-4); font-weight: 500; padding-bottom: 14px; border-bottom: 1px solid var(--rule); margin-bottom: 4px; display: flex; justify-content: space-between; }
.moment { padding: 20px 0; border-bottom: 1px solid var(--rule); display: grid; grid-template-columns: 70px 1fr auto; gap: 18px; align-items: baseline; }
.moment:last-child { border-bottom: none; }
.moment .year { font-family: var(--font-serif); font-style: italic; font-weight: 500; font-size: 26px; color: var(--ox); letter-spacing: -0.01em; line-height: 1; }
.moment .ttl { font-family: var(--font-serif); font-weight: 500; font-size: 19px; line-height: 1.25; color: var(--ink); letter-spacing: -0.005em; }
.moment .ttl em { font-style: italic; }
.moment .ttl small { display: block; font-family: var(--font-sans); font-style: normal; font-weight: 400; font-size: 13.5px; color: var(--ink-3); margin-top: 3px; line-height: 1.4; }
.moment .ref { font-family: var(--font-mono); font-size: 10.5px; color: var(--ink-4); letter-spacing: 0.12em; font-weight: 500; text-transform: uppercase; white-space: nowrap; }
.object-section { padding: 140px 0; background: var(--ink); color: var(--paper); position: relative; overflow: hidden; }
.object-section::before { content: ''; position: absolute; inset: 0; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 0.95 0 0 0 0 0.85 0 0 0 0 0.6 0 0 0 0.04 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); opacity: 0.6; pointer-events: none; }
.object-section .wrap { position: relative; z-index: 1; }
.object-head { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: end; margin-bottom: 84px; }
.object-head h2 { font-family: var(--font-serif); font-weight: 500; font-size: clamp(48px, 6.4vw, 92px); line-height: 0.96; letter-spacing: -0.03em; color: var(--paper); margin: 14px 0 0; text-wrap: balance; }
.object-head h2 em { font-style: italic; color: var(--ox-3); }
.object-head .lede { font-family: var(--font-serif); font-style: italic; font-size: 20px; line-height: 1.55; color: color-mix(in oklab, var(--paper) 78%, transparent); margin: 0; max-width: 42ch; }
.object-eyebrow { font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ox-3); font-weight: 500; }
.features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: color-mix(in oklab, var(--paper) 14%, transparent); border: 1px solid color-mix(in oklab, var(--paper) 14%, transparent); }
.feature { background: var(--ink); padding: 40px 36px 36px; display: flex; flex-direction: column; min-height: 320px; position: relative; }
.feature .fno { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.2em; color: var(--ox-3); font-weight: 500; margin-bottom: 18px; }
.feature h3 { font-family: var(--font-serif); font-weight: 500; font-size: 30px; line-height: 1.05; letter-spacing: -0.015em; color: var(--paper); margin: 0 0 14px; text-wrap: balance; }
.feature h3 em { font-style: italic; color: var(--ox-3); }
.feature p { font-family: var(--font-serif); font-size: 17px; line-height: 1.5; color: color-mix(in oklab, var(--paper) 78%, transparent); margin: 0; font-style: italic; }
.feature .fspecs { margin-top: auto; padding-top: 24px; border-top: 1px solid color-mix(in oklab, var(--paper) 12%, transparent); font-family: var(--font-mono); font-size: 12px; color: color-mix(in oklab, var(--paper) 72%, transparent); letter-spacing: 0.04em; line-height: 1.6; }
.feature .fspecs b { color: var(--paper); font-weight: 500; }
.specs-strip { display: grid; grid-template-columns: repeat(4, 1fr); margin-top: 80px; padding: 40px 0; border-top: 1px solid color-mix(in oklab, var(--paper) 14%, transparent); border-bottom: 1px solid color-mix(in oklab, var(--paper) 14%, transparent); }
.spec { padding: 0 32px; border-right: 1px solid color-mix(in oklab, var(--paper) 10%, transparent); }
.spec:last-child { border-right: none; }
.spec .n { font-family: var(--font-serif); font-weight: 500; font-size: 54px; line-height: 1; letter-spacing: -0.03em; color: var(--paper); }
.spec .n em { font-style: italic; font-weight: 400; color: var(--ox-3); }
.spec .l { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: color-mix(in oklab, var(--paper) 64%, transparent); margin-top: 10px; font-weight: 500; line-height: 1.5; }
.author-line { padding: 124px 0; text-align: left; border-bottom: 1px solid var(--rule); }
.al-grid { display: grid; grid-template-columns: 120px minmax(0, 760px); gap: 48px; align-items: flex-start; max-width: 980px; }
.al-portrait { width: 120px; height: 120px; border-radius: 999px; overflow: hidden; border: 1px solid var(--rule); background: var(--bone-2); flex-shrink: 0; }
.al-portrait img { width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block; }
.al-copy { padding-top: 6px; }
.al-copy .al-eyebrow { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ox); font-weight: 500; margin-bottom: 22px; display: block; }
.al-quote { font-family: var(--font-serif); font-weight: 400; font-style: italic; font-size: clamp(26px, 3.1vw, 42px); line-height: 1.12; letter-spacing: -0.022em; color: var(--ink); margin: 0 0 18px; text-wrap: pretty; max-width: 22ch; }
.al-quote b { font-style: normal; font-weight: 500; color: var(--ox); }
.al-attr { font-family: var(--font-sans); font-size: 14px; color: var(--ink-3); font-weight: 500; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.al-attr b { color: var(--ink); font-weight: 600; }
.al-attr .sep { color: color-mix(in oklab, var(--ink-3) 55%, transparent); }
.al-note { font-family: var(--font-serif); font-style: italic; font-size: 14.5px; line-height: 1.55; color: var(--ink-3); margin: 14px 0 0; max-width: 56ch; }
.readers { padding: 120px 0; background: var(--bone-2); border-bottom: 1px solid var(--rule); }
.readers-head { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 24px 32px; margin-bottom: 56px; padding-bottom: 32px; border-bottom: 1px solid var(--rule); }
.readers-head h2 { font-family: var(--font-serif); font-weight: 500; font-size: clamp(40px, 5vw, 68px); line-height: 1; letter-spacing: -0.02em; color: var(--ink); margin: 0; }
.readers-head h2 em { font-style: italic; color: var(--ox); }
.readers-head .meta { font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.rgrid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.rcard { background: var(--paper); border: 1px solid var(--rule); padding: 32px 28px 28px; border-radius: 2px; position: relative; display: flex; flex-direction: column; }
.rcard .rno { font-family: var(--font-serif); font-style: italic; font-weight: 500; font-size: 28px; line-height: 1; color: var(--ox); letter-spacing: -0.02em; margin: 0 0 16px; }
.rcard .rq { font-family: var(--font-serif); font-size: 19px; line-height: 1.45; color: var(--ink); margin: 0 0 22px; position: relative; z-index: 1; text-wrap: pretty; }
.rcard .rq em { font-style: italic; color: var(--ox); font-weight: 500; }
.rcard .rattr { margin-top: auto; padding-top: 20px; border-top: 1px solid var(--rule); display: flex; justify-content: space-between; align-items: baseline; gap: 14px; }
.rcard .rattr .name { font-family: var(--font-serif); font-weight: 500; font-size: 15.5px; color: var(--ink); }
.inside { padding: 140px 0; background: var(--bone); border-bottom: 1px solid var(--rule); }
.inside-head { display: grid; grid-template-columns: 1.1fr 1fr; gap: 72px; align-items: end; margin-bottom: 56px; }
.inside-head h2 { font-family: var(--font-serif); font-weight: 500; font-size: clamp(48px, 6.4vw, 92px); line-height: 0.96; letter-spacing: -0.03em; color: var(--ink); margin: 14px 0 0; text-wrap: balance; }
.inside-head h2 em { font-style: italic; color: var(--ox); }
.inside-head p { font-family: var(--font-serif); font-style: italic; font-size: 19px; line-height: 1.55; color: var(--ink-2); margin: 0; max-width: 44ch; }
.plates-rail { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(320px, 1fr); gap: 24px; overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory; padding: 4px 32px 40px; margin: 0 -32px 0; scrollbar-width: thin; scrollbar-color: var(--rule-2) transparent; }
.plates-rail::-webkit-scrollbar { height: 8px; }
.plates-rail::-webkit-scrollbar-thumb { background: var(--rule-2); border-radius: 999px; }
.plates-rail::-webkit-scrollbar-track { background: transparent; }
.plate-card { scroll-snap-align: start; display: flex; flex-direction: column; gap: 14px; }
.plate-fig { aspect-ratio: 566 / 800; background: var(--bone-2); border: 1px solid var(--rule); overflow: hidden; position: relative; box-shadow: 0 18px 36px -22px rgba(21,18,13,0.35); }
.plate-fig img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 700ms var(--ease); }
.plate-card:hover .plate-fig img { transform: scale(1.03); }
.plate-fig::after { content: attr(data-no); position: absolute; top: 12px; left: 12px; font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--paper); background: rgba(21,18,13,0.65); backdrop-filter: blur(4px); padding: 5px 9px; border-radius: 2px; font-weight: 500; }
.plate-meta { display: grid; grid-template-columns: 1fr auto; gap: 8px 14px; align-items: baseline; }
.plate-meta .ttl { font-family: var(--font-serif); font-weight: 500; font-size: 19px; line-height: 1.25; color: var(--ink); letter-spacing: -0.005em; }
.plate-meta .ttl em { font-style: italic; color: var(--ox); }
.plate-meta .yr { font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.16em; color: var(--ink-3); font-weight: 500; white-space: nowrap; }
.plate-meta .cap { grid-column: 1 / -1; font-family: var(--font-serif); font-style: italic; font-size: 14.5px; line-height: 1.45; color: var(--ink-3); margin: 0; max-width: 36ch; }
.inside-foot { border-top: 1px solid var(--rule); padding: 28px 0 0; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.inside-foot b { color: var(--ink); font-weight: 600; }
.inside-foot .scroll-hint { display: none; }
.inside-foot .scroll-hint::before { content: '\u2190'; margin-right: 8px; color: var(--ox); }
.inside-foot .scroll-hint::after  { content: '\u2192'; margin-left: 8px; color: var(--ox); }
@media (max-width: 720px) {
  .inside-foot .scroll-hint { display: inline; }
}
.cat-pills { display: flex; flex-wrap: wrap; gap: 0; margin-bottom: 36px; border: 1px solid var(--rule); width: max-content; max-width: 100%; background: var(--paper); }
.cat-pill { appearance: none; background: transparent; border: none; border-right: 1px solid var(--rule); padding: 14px 22px; cursor: pointer; font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; display: flex; align-items: center; gap: 10px; transition: background 200ms var(--ease), color 200ms var(--ease); }
.cat-pill:last-child { border-right: none; }
.cat-pill:hover { background: var(--bone-3); color: var(--ink); }
.cat-pill.on { background: var(--ink); color: var(--paper); }
.cat-pill .swatch { width: 9px; height: 9px; background: var(--swatch, var(--rule-2)); display: inline-block; }
.cat-pill .ct { font-size: 10px; color: var(--ink-4); font-weight: 500; }
.cat-pill.on .ct { color: var(--bone-2); }
.season-rule { display: grid; grid-template-columns: auto 1fr auto; gap: 24px; align-items: center; margin: 56px 0 28px; }
.season-rule:first-of-type { margin-top: 0; }
.season-rule .lbl { font-family: var(--font-serif); font-style: italic; font-size: 28px; color: var(--ink); letter-spacing: -0.01em; }
.season-rule .lbl em { color: var(--ox); }
.season-rule .line { height: 1px; background: var(--rule); }
.season-rule .span { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.16em; color: var(--ink-4); text-transform: uppercase; font-weight: 500; }
.season-toggle { display: none; }
.force-more { display: none; }
.ch-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-top: 1px solid var(--rule); border-left: 1px solid var(--rule); }
.ch-card { appearance: none; background: var(--paper); border: none; border-right: 1px solid var(--rule); border-bottom: 1px solid var(--rule); padding: 22px 22px 24px; text-align: left; cursor: pointer; position: relative; display: flex; flex-direction: column; min-height: 220px; transition: background 200ms var(--ease); }
.ch-card:hover { background: var(--bone-3); }
.ch-card.has-img { padding: 0; overflow: hidden; }
.ch-card.has-img .ch-img-wrap { aspect-ratio: 566 / 800; background: linear-gradient(180deg, var(--bone-3), var(--bone-2)); padding: 12px; overflow: hidden; border-bottom: 1px solid var(--rule); display: flex; align-items: flex-start; justify-content: center; }
.ch-card.has-img .ch-img-wrap img { width: 100%; height: 100%; object-fit: contain; object-position: center top; display: block; box-shadow: 0 16px 28px -20px rgba(21,18,13,0.45); transition: transform 700ms var(--ease); }
.ch-card.has-img:hover .ch-img-wrap img { transform: scale(1.02); }
.ch-card.has-img .ch-body { padding: 18px 22px 22px; flex: 1; display: flex; flex-direction: column; }
.ch-card.previewable { min-height: 420px; overflow: hidden; padding-bottom: 0; }
.ch-body-preview { display: flex; flex-direction: column; z-index: 2; position: relative; }
.ch-card.previewable .ch-sub { margin-bottom: 0; }
.ch-card.previewable .ch-preview { margin: auto -22px 0; padding-top: 10px; width: calc(100% + 44px); flex: 1; min-height: 0; overflow: hidden; position: relative; display: flex; align-items: flex-end; justify-content: center; background: transparent; }
.ch-card.previewable .ch-preview img { width: 100%; height: auto; max-width: 100%; display: block; opacity: 0; transform: translateY(14px) scale(0.985); transition: opacity 240ms var(--ease), transform 360ms var(--ease); transform-origin: bottom center; }
.ch-card.previewable:hover .ch-preview img, .ch-card.previewable:focus-within .ch-preview img { opacity: 1; transform: translateY(0) scale(1); }
.ch-card.dimmed { opacity: 0.32; filter: saturate(0.4); }
.ch-meta-row { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; gap: 12px; }
.ch-num { font-family: var(--font-serif); font-style: italic; font-size: 26px; color: var(--ink); line-height: 1; letter-spacing: -0.02em; }
.ch-cat { font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-4); font-weight: 500; display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.ch-cat .swatch { width: 7px; height: 7px; background: var(--swatch, var(--rule-2)); display: inline-block; }
.ch-title { font-family: var(--font-serif); font-weight: 500; font-size: 17px; line-height: 1.25; letter-spacing: -0.005em; color: var(--ink); margin: 0 0 8px; text-wrap: balance; }
.ch-sub { font-family: var(--font-serif); font-style: italic; font-size: 14px; line-height: 1.4; color: var(--ink-3); margin: 0 0 auto; text-wrap: pretty; }
.ch-date { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.14em; color: var(--ink-4); font-weight: 500; margin-top: auto; padding-top: 10px; border-top: 1px solid var(--rule); }
.ch-card.previewable .ch-date { margin-top: 16px; padding-top: 0; border-top: none; }
.ch-card[data-cat="war"]    { --swatch: var(--ox-2); }
.ch-card[data-cat="mass"]   { --swatch: var(--saffron); }
.ch-card[data-cat="revolt"] { --swatch: var(--ox-3); }
.ch-card[data-cat="gov"]    { --swatch: var(--green); }
.cat-pill[data-cat="war"]    { --swatch: var(--ox-2); }
.cat-pill[data-cat="mass"]   { --swatch: var(--saffron); }
.cat-pill[data-cat="revolt"] { --swatch: var(--ox-3); }
.cat-pill[data-cat="gov"]    { --swatch: var(--green); }
.ch-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--swatch, transparent); opacity: 0; transition: opacity 200ms var(--ease); }
.ch-card:hover::before { opacity: 1; }
.ch-card.has-img::before { z-index: 2; }
.obtain { padding: 140px 0; position: relative; }
.obtain-head { max-width: 920px; margin-bottom: 72px; }
.obtain-head .eyebrow { color: var(--ox); font-weight: 500; margin-bottom: 16px; display: block; }
.obtain-head h2 { font-family: var(--font-serif); font-weight: 500; font-size: clamp(52px, 6.8vw, 100px); line-height: 0.96; letter-spacing: -0.03em; margin: 0 0 20px; color: var(--ink); text-wrap: balance; }
.obtain-head h2 em { font-style: italic; color: var(--ox); }
.obtain-head p { font-family: var(--font-serif); font-style: italic; font-size: 21px; line-height: 1.5; color: var(--ink-2); margin: 0; max-width: 54ch; }
.decide { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; border: 1px solid var(--ink); background: var(--paper); }
.decide-single { grid-template-columns: minmax(0, 720px); justify-content: center; margin: 0 auto; max-width: 720px; }
.choice-solo { padding: 56px 56px 52px; }
.choice { padding: 48px 44px 44px; display: flex; flex-direction: column; position: relative; }
.choice:not(:last-child) { border-right: 1px solid var(--ink); }
.choice-head { display: flex; align-items: baseline; justify-content: space-between; gap: 20px; margin-bottom: 20px; }
.choice-head h3 { font-family: var(--font-serif); font-weight: 500; font-size: 38px; line-height: 1; letter-spacing: -0.02em; color: var(--ink); margin: 0; }
.choice-head h3 em { font-style: italic; color: var(--ox); }
.choice-tag { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; padding: 6px 12px; border: 1px solid var(--rule); border-radius: 999px; white-space: nowrap; flex-shrink: 0; }
.choice .sub { font-family: var(--font-serif); font-style: italic; font-size: 19px; line-height: 1.45; color: var(--ink-2); margin: 0 0 28px; text-wrap: balance; }
.choice .price { display: flex; align-items: baseline; gap: 12px; padding: 24px 0; border-top: 1px solid var(--rule); border-bottom: 1px solid var(--rule); margin: 0 0 24px; }
.choice .price .big { font-family: var(--font-serif); font-weight: 500; font-size: 64px; line-height: 1; letter-spacing: -0.03em; color: var(--ink); }
.choice .price .cur { font-family: var(--font-serif); font-size: 28px; font-weight: 500; color: var(--ox); align-self: flex-start; margin-top: 8px; }
.choice .price .usd { font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; margin-left: auto; }
.choice ul { list-style: none; padding: 0; margin: 0 0 28px; font-family: var(--font-serif); font-size: 17px; color: var(--ink-2); line-height: 1.5; }
.choice ul li { display: grid; grid-template-columns: 20px 1fr; gap: 12px; padding: 7px 0; align-items: baseline; }
.choice ul li::before { content: '\u203A'; color: var(--ox); font-family: var(--font-serif); font-weight: 600; font-size: 20px; line-height: 1; }
.choice .cta { margin-top: auto; width: 100%; justify-content: center; }
.market-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0; margin: 0 0 28px; border-top: 1px solid var(--rule); border-bottom: 1px solid var(--rule); }
.market-card { padding: 18px 0 20px; display: flex; flex-direction: column; gap: 8px; }
.market-card:not(:last-child) { border-right: 1px solid var(--rule); padding-right: 18px; margin-right: 18px; }
.market-label { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.market-price { font-family: var(--font-serif); font-size: 31px; line-height: 1; color: var(--ink); letter-spacing: -0.02em; }
.market-meta { font-family: var(--font-serif); font-size: 14.5px; line-height: 1.45; color: var(--ink-3); min-height: 2.9em; }
.market-links { display: flex; flex-wrap: wrap; gap: 8px; }
.store-link { display: inline-flex; align-items: center; justify-content: center; min-height: 34px; padding: 8px 12px; border: 1px solid var(--rule); border-radius: 999px; font-family: var(--font-sans); font-size: 13px; font-weight: 600; color: var(--ink-3); text-decoration: none; transition: border-color 180ms var(--ease), color 180ms var(--ease), background 180ms var(--ease); }
.store-link:hover { border-color: var(--ox); color: var(--ox); background: color-mix(in oklab, var(--ox) 5%, transparent); }
.obtain-foot { margin-top: 28px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; font-family: var(--font-mono); font-size: 11.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.obtain-foot b { color: var(--ox); font-weight: 600; }
.obtain-foot .guarantees { display: flex; gap: 26px; flex-wrap: wrap; }
.obtain-foot .guarantees span::before { content: '\u25CF'; color: var(--ox); margin-right: 8px; font-size: 8px; vertical-align: 2px; }
footer { background: var(--ink); color: var(--paper); padding: 80px 0 36px; position: relative; z-index: 1; overflow: hidden; }
footer::before { content: ''; position: absolute; inset: 0; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='9'/><feColorMatrix values='0 0 0 0 0.95 0 0 0 0 0.85 0 0 0 0 0.6 0 0 0 0.04 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); opacity: 0.5; pointer-events: none; }
.foot-inner { position: relative; z-index: 1; }
.foot-top { display: grid; grid-template-columns: 2fr 1fr; gap: 56px; margin-bottom: 56px; }
.foot-brand .brand { color: var(--paper); font-size: 28px; }
.foot-brand .brand em { color: var(--ox-3); }
.foot-brand p { font-family: var(--font-serif); font-style: italic; font-size: 17px; line-height: 1.55; color: color-mix(in oklab, var(--paper) 75%, transparent); margin: 16px 0 0; max-width: 34ch; }
.foot-top h4 { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ox-3); margin: 0 0 16px; font-weight: 500; }
.foot-top a { display: block; font-family: var(--font-sans); font-size: 15px; color: color-mix(in oklab, var(--paper) 82%, transparent); text-decoration: none; margin-bottom: 10px; transition: color 150ms; }
.foot-top a:hover { color: var(--ox-3); }
.foot-bottom { padding-top: 28px; border-top: 1px solid color-mix(in oklab, var(--paper) 14%, transparent); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: color-mix(in oklab, var(--paper) 60%, transparent); font-weight: 500; }
.mobile-buy-bar { display: none; }
.fade-up { opacity: 0; transform: translateY(14px); animation: fadeUp 900ms var(--ease-out) forwards; }
.d1 { animation-delay: 60ms; } .d2 { animation-delay: 140ms; } .d3 { animation-delay: 240ms; } .d4 { animation-delay: 360ms; } .d5 { animation-delay: 500ms; }
@keyframes fadeUp { to { opacity: 1; transform: translateY(0); } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } .ticker-track { animation: none !important; } }
@media (max-width: 1080px) {
  .hero-grid { grid-template-columns: 1fr; gap: 56px; }
  .hero-right { justify-content: flex-start; }
  .force-panel { grid-template-columns: 1fr; }
  .force-copy { border-right: none; border-bottom: 1px solid var(--rule); }
  .specs-strip { grid-template-columns: repeat(2, 1fr); gap: 28px 0; }
  .spec { padding: 16px 24px; border-right: none; border-top: 1px solid color-mix(in oklab, var(--paper) 10%, transparent); }
  .features { grid-template-columns: 1fr; }
  .rgrid { grid-template-columns: 1fr; }
  .decide { grid-template-columns: 1fr; }
  .choice { border-right: none !important; border-bottom: 1px solid var(--ink); }
  .choice:last-child { border-bottom: none; }
  .forces-head, .object-head, .inside-head { grid-template-columns: 1fr; gap: 32px; }
  .plates-rail { grid-auto-columns: minmax(260px, 1fr); }
  .ch-grid { grid-template-columns: repeat(2, 1fr); }
  .foot-top { grid-template-columns: 1fr 1fr; gap: 32px; }
  .market-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .market-card:nth-child(2n) { border-right: none; padding-right: 0; margin-right: 0; padding-left: 18px; margin-left: 18px; }
  .market-card:nth-child(2n+1) { border-right: 1px solid var(--rule); padding-right: 18px; margin-right: 18px; }
  .market-card:nth-child(n+3) { border-top: 1px solid var(--rule); padding-top: 18px; }
}
@media (max-width: 720px) {
  .ef2-root { padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px)); }
  .wrap { padding: 0 20px; }
  .ch-grid { grid-template-columns: none; grid-auto-flow: column; grid-auto-columns: 86%; gap: 12px; overflow-x: auto; border: none; padding-bottom: 4px; scrollbar-width: none; }
  .ch-grid::-webkit-scrollbar { display: none; }
  .nav-inner { padding: 14px 20px; }
  .nav-links { display: none; }
  .nav-mobile { display: block; }
  .nav-mobile .wrap { gap: 8px; padding-top: 8px; padding-bottom: 10px; }
  .nav-mobile a { padding: 8px 11px; font-size: 10px; letter-spacing: 0.14em; }
  .brand { font-size: 20px; }
  .btn-sm { padding: 10px 14px; font-size: 12px; }
  .hero { padding: 24px 0 34px; }
  .hero-grid { gap: 20px; }
  .hero-tag { margin-bottom: 18px; font-size: 10.5px; letter-spacing: 0.16em; gap: 12px; padding: 7px 12px; }
  .ef2-root .hero-title { font-size: clamp(52px, 18vw, 78px); line-height: 0.94; max-width: 5.8ch; }
  .hero-sub { font-size: 17px; line-height: 1.38; margin-top: 18px; max-width: 24ch; }
  .hero-right { justify-content: center; }
  .cover-plate { max-width: min(72vw, 300px); margin: 0 auto; }
  .plate-corner { font-size: 9px; letter-spacing: 0.14em; }
  .plate-corner.tl, .plate-corner.tr { top: -18px; }
  .plate-corner.bl, .plate-corner.br { bottom: -18px; }
  .hero-ctas { margin-top: 18px; gap: 10px; }
  .ticker { padding: 14px 0; }
  .ticker-track { font-size: 21px; }
  .ticker-track .dot { margin: 0 18px; }
  .ticker-track .year { font-size: 15px; margin-right: 8px; }
  .force-tabs { grid-template-columns: repeat(2, 1fr); }
  .force-tab { border-right: 1px solid var(--rule); border-bottom: 1px solid var(--rule); }
  .force-tab:nth-child(2n) { border-right: none; }
  .force-tab { padding: 18px 16px 16px; gap: 8px; }
  .force-tab .tnum { font-size: 9.5px; letter-spacing: 0.14em; }
  .force-tab .tname { font-size: 22px; }
  .force-tab .tspan { font-size: 10px; }
  .hero-stat-row { grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 22px; padding-top: 16px; }
  .hero-stat .n { font-size: 30px; }
  .hero-stat .l { font-size: 8.5px; letter-spacing: 0.11em; margin-top: 5px; max-width: 12ch; }
  .hero-ctas { align-items: stretch; }
  .hero-ctas .btn { width: 100%; justify-content: center; }
  .hero-ctas .meta { width: 100%; margin-left: 0; text-align: center; }
  .forces, .object-section, .inside, .author-line, .readers, .obtain { padding: 76px 0; }
  .forces-head, .object-head, .inside-head { gap: 18px; margin-bottom: 38px; }
  .forces-head h2, .object-head h2, .inside-head h2, .readers-head h2, .obtain-head h2 { font-size: clamp(42px, 14vw, 64px); line-height: 0.98; margin-top: 10px; }
  .forces-head p, .object-head .lede, .inside-head p, .obtain-head p { font-size: 17px; line-height: 1.48; max-width: 22ch; }
  .force-panel { min-height: unset; }
  .force-copy { padding: 28px 22px 26px; }
  .force-copy .fnum { font-size: 112px; top: 18px; right: 18px; }
  .force-copy h3 { font-size: 34px; margin-bottom: 16px; }
  .force-copy .flede { font-size: 17px; margin-bottom: 24px; }
  .force-copy .fmeta { grid-template-columns: 1fr; gap: 12px; padding-top: 18px; }
  .force-moments { padding: 24px 22px; }
  .force-moments .mhead { font-size: 9.5px; letter-spacing: 0.18em; padding-bottom: 12px; }
  .moment { grid-template-columns: 54px 1fr; gap: 12px; padding: 16px 0; }
  .moment .year { font-size: 22px; }
  .moment .ttl { font-size: 17px; }
  .moment .ttl small { font-size: 12.5px; margin-top: 4px; }
  .moment .ref { display: none; }
  .force-more { display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-top: 14px; padding: 11px 16px; border: 1px solid var(--rule); background: var(--paper); color: var(--ink); font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 500; align-self: flex-start; }
  .force-more .arrow { color: var(--ox); font-size: 12px; line-height: 1; }
  .feature { padding: 28px 22px 24px; min-height: unset; }
  .features { display: grid; grid-template-columns: none; grid-auto-flow: column; grid-auto-columns: 86%; gap: 12px; overflow-x: auto; padding-bottom: 4px; background: none; border: none; scrollbar-width: none; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }
  .features::-webkit-scrollbar { display: none; }
  .features .feature { scroll-snap-align: start; min-width: 0; }
  .feature { border: 1px solid color-mix(in oklab, var(--paper) 14%, transparent); min-height: 248px; }
  .feature h3 { font-size: 26px; }
  .feature p { font-size: 16px; }
  .feature .fspecs { padding-top: 18px; font-size: 11px; }
  .specs-strip { margin-top: 34px; padding: 0 0 4px; grid-template-columns: none; grid-auto-flow: column; grid-auto-columns: 72%; gap: 12px; overflow-x: auto; border: none; scrollbar-width: none; }
  .specs-strip::-webkit-scrollbar { display: none; }
  .spec { padding: 18px 18px 14px; border: 1px solid color-mix(in oklab, var(--paper) 14%, transparent); background: color-mix(in oklab, var(--paper) 2%, transparent); }
  .spec .n { font-size: 38px; }
  .spec .l { font-size: 9px; letter-spacing: 0.12em; }
  .cat-pills { width: auto; margin-left: -20px; margin-right: -20px; padding: 0 20px; overflow-x: auto; flex-wrap: nowrap; scrollbar-width: none; }
  .cat-pills::-webkit-scrollbar { display: none; }
  .cat-pill { flex: 0 0 auto; padding: 12px 14px; font-size: 9.5px; letter-spacing: 0.14em; gap: 8px; }
  .cat-pill .ct { font-size: 9px; }
  .season-rule { grid-template-columns: 1fr; gap: 6px; align-items: start; margin: 34px 0 18px; }
  .season-rule .line { display: none; }
  .season-rule .lbl { font-size: 24px; }
  .season-rule .span { font-size: 9.5px; letter-spacing: 0.12em; }
  .season-toggle { display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-top: 14px; padding: 11px 16px; border: 1px solid var(--rule); background: var(--paper); color: var(--ink); font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 500; }
  .season-toggle .arrow { color: var(--ox); font-size: 12px; line-height: 1; }
  .ch-card { padding: 18px 18px 20px; min-height: 200px; border: 1px solid var(--rule); }
  .ch-card.has-img .ch-body { padding: 16px 18px 18px; }
  .ch-card.has-img .ch-img-wrap { padding: 10px; }
  .ch-card.previewable { min-height: auto; padding-bottom: 18px; }
  .ch-num { font-size: 22px; }
  .ch-title { font-size: 16px; margin-bottom: 6px; }
  .ch-sub { font-size: 13px; }
  .ch-date { font-size: 9.5px; letter-spacing: 0.12em; padding-top: 9px; }
  .ch-card.previewable .ch-preview { margin: 12px -18px 0; padding-top: 0; width: calc(100% + 36px); flex: none; min-height: auto; }
  .ch-card.previewable .ch-preview img { opacity: 1; transform: none; }
  .inside-foot { padding-top: 18px; font-size: 9.5px; letter-spacing: 0.12em; gap: 10px; }
  .author-line { padding: 70px 0; }
  .al-grid { grid-template-columns: 1fr; gap: 18px; }
  .al-copy { padding-top: 0; }
  .al-copy .al-eyebrow { margin-bottom: 14px; }
  .al-quote { font-size: clamp(24px, 11vw, 34px); line-height: 1.08; max-width: 18ch; margin-bottom: 14px; }
  .al-attr { font-size: 13px; gap: 8px; }
  .readers { padding-top: 72px; }
  .readers-head { gap: 12px; margin-bottom: 28px; padding-bottom: 18px; }
  .readers-head .meta { font-size: 9.5px; letter-spacing: 0.14em; }
  .rgrid { display: grid; grid-template-columns: none; grid-auto-flow: column; grid-auto-columns: 88%; gap: 12px; overflow-x: auto; padding-bottom: 4px; scrollbar-width: none; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }
  .rgrid::-webkit-scrollbar { display: none; }
  .rgrid .rcard { scroll-snap-align: start; min-width: 0; }
  .rcard { padding: 24px 20px 20px; min-height: 220px; }
  .rcard .rq { font-size: 17px; margin-bottom: 16px; }
  .rcard .rattr { padding-top: 14px; gap: 10px; align-items: flex-start; flex-direction: column; }
  .obtain-head { margin-bottom: 34px; }
  .decide-single { max-width: none; }
  .choice-solo, .choice { padding: 26px 20px 24px; min-width: 0; }
  .decide, .decide-single { min-width: 0; }
  .choice-head { gap: 12px; margin-bottom: 14px; align-items: flex-start; }
  .choice-head h3 { font-size: 28px; }
  .choice-tag { font-size: 9px; letter-spacing: 0.14em; padding: 5px 10px; }
  .choice .sub { font-size: 15px; line-height: 1.44; margin-bottom: 18px; }
  .choice .price { padding: 16px 0; margin-bottom: 16px; gap: 10px; }
  .choice .price .cur { font-size: 22px; margin-top: 6px; }
  .choice .price .big { font-size: 52px; }
  .choice .price .usd { font-size: 10px; letter-spacing: 0.14em; line-height: 1.5; margin-left: 0; }
  .choice ul { font-size: 15px; margin-bottom: 18px; }
  .choice ul li { grid-template-columns: 16px 1fr; gap: 10px; padding: 5px 0; }
  .market-grid { grid-template-columns: none; grid-auto-flow: column; grid-auto-columns: 86%; gap: 12px; overflow-x: auto; border: none; margin: 0 0 20px; padding-bottom: 4px; scrollbar-width: none; min-width: 0; width: 100%; max-width: 100%; -webkit-overflow-scrolling: touch; scroll-snap-type: x mandatory; }
  .market-card { scroll-snap-align: start; min-width: 0; }
  .market-grid::-webkit-scrollbar { display: none; }
  .foot-top { grid-template-columns: 1fr; }
  .market-card, .market-card:nth-child(2n), .market-card:nth-child(2n+1) { border: 1px solid var(--rule); border-right: 1px solid var(--rule); border-top: 1px solid var(--rule); padding: 14px 14px 16px; margin: 0; background: var(--bone-3); }
  .market-card:not(:last-child) { border-bottom: 1px solid var(--rule); }
  .market-price { font-size: 28px; }
  .market-meta { font-size: 13.5px; min-height: auto; }
  .market-links { gap: 6px; }
  .store-link { font-size: 12px; padding: 7px 10px; min-height: 32px; }
  .obtain-foot { margin-top: 18px; font-size: 9px; letter-spacing: 0.12em; gap: 10px; }
  .obtain-foot .guarantees { gap: 12px; }
  .foot-top { gap: 20px; margin-bottom: 30px; }
  .foot-brand .brand { font-size: 24px; }
  .foot-brand p { font-size: 15px; max-width: 26ch; }
  .foot-top a { font-size: 14px; margin-bottom: 8px; }
  .foot-bottom { padding-top: 18px; font-size: 9px; letter-spacing: 0.14em; }
  .mobile-buy-bar { position: fixed; left: 12px; right: 12px; bottom: calc(12px + env(safe-area-inset-bottom, 0px)); z-index: 60; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; border: 1px solid color-mix(in oklab, var(--ink) 10%, transparent); background: color-mix(in oklab, var(--paper) 88%, rgba(255,255,255,0.65)); backdrop-filter: blur(16px); box-shadow: 0 18px 36px -24px rgba(21,18,13,0.42); }
  .mobile-buy-bar .copy { min-width: 0; }
  .mobile-buy-bar .eyebrow { display: block; font-family: var(--font-mono); font-size: 8.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-4); font-weight: 500; margin-bottom: 3px; }
  .mobile-buy-bar .line { font-family: var(--font-serif); font-size: 17px; line-height: 1; color: var(--ink); letter-spacing: -0.015em; white-space: nowrap; }
  .mobile-buy-bar .line em { font-style: italic; color: var(--ox); }
  .mobile-buy-bar .btn { flex: 0 0 auto; padding: 12px 16px; font-size: 13px; min-height: 44px; }
}
@media (max-width: 420px) {
  .ef2-root .hero-title { font-size: 48px; }
  .hero-sub { font-size: 16px; max-width: 23ch; }
  .hero-stat .n { font-size: 32px; }
  .forces-head h2, .object-head h2, .inside-head h2, .readers-head h2, .obtain-head h2 { font-size: 40px; }
  .choice .price .big { font-size: 46px; }
  .market-price { font-size: 26px; }
  .mobile-buy-bar .line { font-size: 15px; }
  .mobile-buy-bar .btn { padding: 11px 14px; font-size: 12px; }
}
`;

// ─── Data ────────────────────────────────────────────────────────────────────

const SEASONS = [{
  id: 's1',
  no: 'I',
  kind: 'Season I — Company & Conquest',
  label: 'Company to Raj',
  span: '1600–1900',
  title: <>Trade turns into <em>empire</em></>,
  lede: "Chapters 1 — 13 trace the East India Company from its 1600 charter to its arrival at Surat, the rise of the presidencies, Plassey, Buxar, Mysore, the Maratha wars, 1857, the transfer of power from Company to Crown, and the consolidation of the Raj.",
  meta: [{
    l: 'Chapters',
    v: <><em>1 — 13</em></>
  }, {
    l: 'Arc',
    v: <>Charter to <em>Raj</em></>
  }],
  moments: [{
    year: 1600,
    t: 'East India Company chartered',
    s: 'Elizabeth I grants a royal charter to a London guild of merchants.',
    ref: 'Ch. 1'
  }, {
    year: 1757,
    t: <>Battle of <em>Plassey</em></>,
    s: 'A decisive Company-backed victory in Bengal changes the balance of power.',
    ref: 'Ch. 5'
  }, {
    year: 1799,
    t: <>Fall of <em>Tipu Sultan</em></>,
    s: 'The Anglo-Mysore struggle ends with Srirangapatna and a reshaped south.',
    ref: 'Ch. 7'
  }, {
    year: 1857,
    t: 'Rebellion and rupture',
    s: 'Sepoy grievances, Awadh, Delhi, and revolt bring Company rule to an end.',
    ref: 'Ch. 11'
  }]
}, {
  id: 's2',
  no: 'II',
  kind: 'Season II — Nationalism & Freedom',
  label: 'Raj to Freedom',
  span: '1870–1947',
  title: <>Nationalism moves toward <em>freedom</em></>,
  lede: "Chapters 14 — 24 follow the press, Congress, Swadeshi, reform, Jallianwala Bagh, Gandhi, Bhagat Singh, civil disobedience, Ambedkar, Bose, Quit India, the INA, partition, and independence.",
  meta: [{
    l: 'Chapters',
    v: <><em>14 — 24</em></>
  }, {
    l: 'Arc',
    v: <>Congress to <em>Midnight</em></>
  }],
  moments: [{
    year: 1885,
    t: 'Indian National Congress',
    s: 'Educated Indians, newspapers, and public associations reshape politics.',
    ref: 'Ch. 14'
  }, {
    year: 1919,
    t: 'Jallianwala Bagh',
    s: 'Massacre and non-cooperation alter the course of nationalist politics.',
    ref: 'Ch. 17'
  }, {
    year: 1930,
    t: <>The <em>Salt March</em></>,
    s: 'Civil disobedience turns salt into a national symbol of resistance.',
    ref: 'Ch. 19'
  }, {
    year: 1947,
    t: <>Independence and <em>partition</em></>,
    s: 'Freedom arrives with borders, displacement, and unresolved grief.',
    ref: 'Ch. 23'
  }]
}];
const TICKER = [{
  y: 1600,
  t: 'Company chartered'
}, {
  y: 1639,
  t: 'Madras founded'
}, {
  y: 1757,
  t: 'Plassey'
}, {
  y: 1765,
  t: 'Diwani of Bengal'
}, {
  y: 1799,
  t: 'Fall of Tipu Sultan'
}, {
  y: 1818,
  t: 'Maratha wars end'
}, {
  y: 1857,
  t: 'The Great Rebellion'
}, {
  y: 1858,
  t: 'Company \u2192 Crown'
}, {
  y: 1885,
  t: 'Congress formed'
}, {
  y: 1919,
  t: 'Jallianwala Bagh'
}, {
  y: 1930,
  t: 'Salt March'
}, {
  y: 1942,
  t: 'Quit India'
}, {
  y: 1947,
  t: 'Independence'
}];
const FEATURES = [{
  no: '01',
  title: <>History told through <em>scenes</em></>,
  body: "Each chapter is built around one illustrated plate: a coastline, a courtroom, a cannon, a march. The book uses maps, portraits, timelines, and short notes to make the long arc easier to hold in memory.",
  specs: <><b>24 chapters</b> · maps · timelines · visual notes</>
}, {
  no: '02',
  title: <>A clear route through <em>350 years</em></>,
  body: "The ebook compresses a long, turbulent period into an accessible visual sequence, from the East India Company's first foothold to the end of empire and the birth of independent India.",
  specs: <><b>1600-1947</b> · Company · Crown · freedom movement</>
}, {
  no: '03',
  title: <>Made to browse, <em>then revisit</em></>,
  body: "Read straight through, or jump by chapter, moment, or movement. The structure helps readers trace conquest, governance, mass politics, and resistance across time.",
  specs: <><b>31 visual pages</b> · ebook edition · full colour</>
}];
const SPECS = [{
  n: <em>350</em>,
  l: 'Years of history, 1600-1947'
}, {
  n: '24',
  l: 'Chapters across two seasons'
}, {
  n: <em>31</em>,
  l: 'Visual pages in the ebook'
}, {
  n: '4',
  l: 'Colour-coded threads through time'
}];
const CHAPTERS_INDEX = [{
  n: 1,
  cat: 'war',
  title: 'When the East India Company Reached India',
  sub: 'A chartered company lands on a distant coast.',
  date: '1600 — 1613',
  season: 1,
  img: 'https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2RBkUqh3LVjYlDQLDGXc6cEkFC7/ch01-w49h3uc9rc.jpg'
}, {
  n: 2,
  cat: 'war',
  title: 'From Landing to Foothold',
  sub: 'Trading posts, fortified factories, first blood.',
  date: '1608 — 1615',
  season: 1
}, {
  n: 3,
  cat: 'gov',
  title: 'From Surat to the Three Presidencies',
  sub: 'Bombay, Madras, Calcutta — a triangle of power.',
  date: '1639 — 1700',
  season: 1
}, {
  n: 4,
  cat: 'war',
  title: 'Cracks in the Empire',
  sub: 'Marathas, Nawabs & Nadir Shah shake the Mughal throne.',
  date: '1700 — 1740',
  season: 1
}, {
  n: 5,
  cat: 'war',
  title: 'From Trade to Power',
  sub: 'Plassey, Buxar & the Diwani — a company becomes a state.',
  date: '1740 — 1765',
  season: 1,
  img: 'https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2RBkUqh3LVjYlDQLDGXc6cEkFC7/ch05-plassey-gtwmiarcbld.jpg'
}, {
  n: 6,
  cat: 'gov',
  title: 'Ruling Bengal',
  sub: 'Famine, Hastings & the first laws of Company Raj.',
  date: '1765 — 1793',
  season: 1
}, {
  n: 7,
  cat: 'war',
  title: 'Southward Expansion',
  sub: 'Anglo-Mysore wars and the fall of Tipu Sultan.',
  date: '1767 — 1799',
  season: 1
}, {
  n: 8,
  cat: 'war',
  title: 'Breaking the Maratha Confederacy',
  sub: 'The last great Indian power, unmade.',
  date: '1802 — 1818',
  season: 1
}, {
  n: 9,
  cat: 'war',
  title: 'Frontier Wars',
  sub: 'Sindh, Punjab & the edge of empire.',
  date: '1830 — 1849',
  season: 1
}, {
  n: 10,
  cat: 'gov',
  title: 'Engines of Rule',
  sub: 'Dalhousie, Doctrine of Lapse & the New Technologies of Empire.',
  date: '1848 — 1856',
  season: 1
}, {
  n: 11,
  cat: 'revolt',
  title: 'The Fuse Is Lit',
  sub: 'Awadh, Cartridges & Sepoy Grievances.',
  date: '1840s — 1857',
  season: 1
}, {
  n: 12,
  cat: 'revolt',
  title: '1857 — Rebellion',
  sub: 'Rebellion, Siege & the End of Company Rule.',
  date: '1857 — 1858',
  season: 1
}, {
  n: 13,
  cat: 'gov',
  title: 'Death of the Company',
  sub: 'Birth of the Raj. The Crown takes over.',
  date: '1858 — 1874',
  season: 2
}, {
  n: 14,
  cat: 'mass',
  title: 'Seeds of Nationalism',
  sub: 'Educated India, the press & the birth of Congress.',
  date: '1870 — 1885',
  season: 2
}, {
  n: 15,
  cat: 'mass',
  title: 'Partition & Protest',
  sub: 'Swadeshi, the Muslim League & new reforms.',
  date: '1905 — 1909',
  season: 2
}, {
  n: 16,
  cat: 'gov',
  title: 'War & Promises',
  sub: 'Delhi Durbar, Home Rule & the Montagu Declaration.',
  date: '1911 — 1919',
  season: 2
}, {
  n: 17,
  cat: 'mass',
  title: 'Massacre & Mahatma',
  sub: 'Jallianwala Bagh & Non-Cooperation.',
  date: '1919 — 1922',
  season: 2
}, {
  n: 18,
  cat: 'revolt',
  title: 'Revolutionary Fire',
  sub: 'Bhagat Singh, HSRA & the philosophy of sacrifice.',
  date: '1926 — 1931',
  season: 2
}, {
  n: 19,
  cat: 'mass',
  title: 'Salt & Representation',
  sub: 'Civil Disobedience & the Poona Pact.',
  date: '1930 — 1934',
  season: 2
}, {
  n: 20,
  cat: 'gov',
  title: 'Ballot & Provinces',
  sub: 'Government of India Act 1935 & the 1937 elections.',
  date: '1935 — 1939',
  season: 2
}, {
  n: 21,
  cat: 'revolt',
  title: 'War & Quit India',
  sub: 'Bose, the INA & the 1942 uprising.',
  date: '1939 — 1945',
  season: 2
}, {
  n: 22,
  cat: 'gov',
  title: 'Two Nations?',
  sub: 'Lahore Resolution, Cabinet Mission & communal fury.',
  date: '1940 — 1946',
  season: 2
}, {
  n: 23,
  cat: 'mass',
  title: 'Freedom & Partition',
  sub: 'Independence. Refugees. A line drawn in a hurry.',
  date: '1947 — 1948',
  season: 2
}, {
  n: 24,
  cat: 'mass',
  title: 'A 350-Year Journey',
  sub: 'Empire & Freedom — recap.',
  date: '1600 — 1947',
  season: 2
}];
const CAT_LABEL = {
  war: 'Wars & Conquest',
  mass: 'Mass Movements',
  revolt: 'Revolutionary Resistance',
  gov: 'Government & Crown'
};
const READERS = [{
  q: <>For readers who want the <em>shape</em> of the story before going deeper into colonial India, nationalism, partition, and independence.</>,
  name: <>General readers</>
}, {
  q: <>For students who need timelines, turning points, and visual anchors before moving into denser texts or classroom material.</>,
  name: <>Students</>
}, {
  q: <>For families and classrooms looking for a concise visual introduction to a difficult, consequential period of history.</>,
  name: <>Families &amp; classrooms</>
}];
const STORE_LINKS = {
  us: 'https://www.amazon.com/dp/B0G52ZY3W1',
  india: 'https://www.amazon.in/dp/B0G52ZY3W1',
  uk: 'https://www.amazon.co.uk/dp/B0G52ZY3W1',
  de: 'https://www.amazon.de/dp/B0G52ZY3W1',
  fr: 'https://www.amazon.fr/dp/B0G52ZY3W1',
  es: 'https://www.amazon.es/dp/B0G52ZY3W1',
  it: 'https://www.amazon.it/dp/B0G52ZY3W1',
  nl: 'https://www.amazon.nl/dp/B0G52ZY3W1'
};

// ─── Components ──────────────────────────────────────────────────────────────

function Nav() {
  return <nav className="nav">
      <div className="nav-inner">
        <a href="#" className="brand" data-tracking-id="link-09048e24c5ac1aa1">Empire <em>&amp;</em> Freedom</a>
        <div className="nav-links">
          <a href="#seasons" data-tracking-id="link-f8b4a8061af686c3">Seasons</a>
          <a href="#object" data-tracking-id="link-df17395d8846002d">The Book</a>
          <a href="#inside" data-tracking-id="link-9ef70ee9fb900093">Chapters</a>
          <a href="#readers" data-tracking-id="link-d8dc5184d64f6890">Readers</a>
          <a href="#obtain" data-tracking-id="link-099a21ca4814d277">Buy</a>
        </div>
        <a href="#obtain" className="btn btn-primary btn-sm" data-tracking-id="link-f551e26eaee84854">Buy the ebook <span className="arrow">&rarr;</span></a>
      </div>
      <div className="nav-mobile">
        <div className="wrap">
          <a href="#seasons" data-tracking-id="link-d8eacb3392b1931d">Seasons</a>
          <a href="#object" data-tracking-id="link-4ac8c9e8c0cf1d62">The Book</a>
          <a href="#inside" data-tracking-id="link-6cf775acd87b637f">Chapters</a>
          <a href="#readers" data-tracking-id="link-7fed3bec798103a0">Readers</a>
          <a href="#obtain" data-tracking-id="link-94c10b6a9e33eb5f">Buy</a>
        </div>
      </div>
    </nav>;
}
function Hero() {
  return <section className="hero">
      <div className="wrap">
        <div className="hero-grid">
          <div className="hero-left">
            <div className="hero-tag fade-up d1">
              <span className="pulse"></span>
              Ebook edition &middot; Out now
            </div>
            <h1 className="hero-title fade-up d2">
              Empire
              <span className="amp"> &amp; </span>
              <span className="italic">Freedom.</span>
            </h1>
            <p className="hero-sub fade-up d3">
              A <b>visual history</b> of the East India Company, the British Raj, and the Indian independence movement — told in 24 illustrated chapters.
            </p>
            <div className="hero-ctas fade-up d4">
              <a href="#obtain" className="btn btn-ox btn-lg" data-tracking-id="link-fcd8d63e5f8bcd90">Buy the ebook <span className="arrow">&rarr;</span></a>
              <a href="#inside" className="btn btn-ghost btn-lg" data-tracking-id="link-b88f130a558f7aa1">See the chapters</a>
              <span className="meta">From <b>$19.47</b> on Kindle</span>
            </div>
            <div className="hero-stat-row fade-up d5">
              <div className="hero-stat">
                <div className="n">24</div>
                <div className="l">Chapters across two seasons</div>
              </div>
              <div className="hero-stat">
                <div className="n"><em>2</em></div>
                <div className="l">Narrative arcs, empire &amp; freedom</div>
              </div>
              <div className="hero-stat">
                <div className="n"><em>350</em></div>
                <div className="l">Years of history, 1600–1947</div>
              </div>
            </div>
          </div>
          <div className="hero-right fade-up d3">
            <div className="cover-plate">
              <span className="plate-corner tl">First Edition · Ebook</span>
              <span className="plate-corner tr">1600 — 1947</span>
              <div className="plate-mark tl"></div>
              <div className="plate-mark tr"></div>
              <div className="plate-mark bl"></div>
              <div className="plate-mark br"></div>
              <img className="cover-img" src="https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2RBkUqh3LVjYlDQLDGXc6cEkFC7/cover-ww5w0xtvzkq.jpg" alt="Empire &amp; Freedom cover" />
              <span className="plate-corner bl">24 chapters · 31 visual pages</span>
              <span className="plate-corner br">Bhanu Sharma · 2026</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function TickerBar() {
  const items = [...TICKER, ...TICKER];
  return <div className="ticker" aria-hidden="true">
      <div className="ticker-track">
        {items.map((item, i) => <span key={i}>
            <span className="year">{item.y}</span>
            {item.t}
            <span className="dot">✦</span>
          </span>)}
      </div>
    </div>;
}
function Seasons() {
  const [active, setActive] = useState('s1');
  const [showMore, setShowMore] = useState({
    s1: false,
    s2: false
  });
  const season = SEASONS.find(s => s.id === active);
  const visibleMoments = showMore[active] ? season.moments : season.moments.slice(0, 3);
  return <section className="forces" id="seasons">
      <div className="wrap">
        <div className="forces-head">
          <div>
            <div className="eyebrow">The two seasons</div>
            <h2>Empire <em>&amp;</em><br />Freedom</h2>
          </div>
          <p>
            Two narrative arcs spanning 350 years — from the East India Company's first charter to independence in 1947. Each season tracks a different force shaping the subcontinent.
          </p>
        </div>

        <div className="force-tabs">
          {SEASONS.map(s => <button key={s.id} className={`force-tab${active === s.id ? ' active' : ''}`} data-f={s.id} onClick={() => setActive(s.id)} data-tracking-id={`btn-118be212865c26b6_${s.id}`}>
              <span className="tnum">Season {s.no}</span>
              <span className="tname">{s.label}</span>
              <span className="tspan">{s.span}</span>
            </button>)}
        </div>

        <div className="force-panel">
          <div className="force-copy">
            <div className="fnum" aria-hidden="true">{season.no}</div>
            <div className="fkind">{season.kind}</div>
            <h3>{season.title}</h3>
            <p className="flede">{season.lede}</p>
            <div className="fmeta">
              {season.meta.map((m, i) => <div key={i}>
                  <div className="mlbl">{m.l}</div>
                  <div className="mval">{m.v}</div>
                </div>)}
            </div>
          </div>

          <div className="force-moments">
            <div className="mhead">
              <span>Key moments</span>
              <span>{season.span}</span>
            </div>
            {visibleMoments.map((m, i) => <div className="moment" key={i}>
                <div className="year">{m.year}</div>
                <div className="ttl">
                  {m.t}
                  <small>{m.s}</small>
                </div>
                <div className="ref">{m.ref}</div>
              </div>)}
            {season.moments.length > 3 && !showMore[active] && <button className="force-more" onClick={() => setShowMore(prev => ({
            ...prev,
            [active]: true
          }))} data-tracking-id="btn-c428b83e2792cbb1">
                Show more <span className="arrow">→</span>
              </button>}
          </div>
        </div>
      </div>
    </section>;
}
function TheObject() {
  return <section className="object-section" id="object">
      <div className="wrap">
        <div className="object-head">
          <div>
            <div className="object-eyebrow">The ebook</div>
            <h2>What's<br /><em>inside</em></h2>
          </div>
          <p className="lede">
            A 31-page visual ebook built around illustrated plates, timelines, and short narrative chapters — designed to make 350 years of history legible in a single reading.
          </p>
        </div>

        <div className="features">
          {FEATURES.map((f, i) => <div className="feature" key={i}>
              <div className="fno">{f.no}</div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
              <div className="fspecs">{f.specs}</div>
            </div>)}
        </div>

        <div className="specs-strip">
          {SPECS.map((s, i) => <div className="spec" key={i}>
              <div className="n">{s.n}</div>
              <div className="l">{s.l}</div>
            </div>)}
        </div>
      </div>
    </section>;
}
function Inside() {
  const [activeCat, setActiveCat] = useState('all');
  const [collapsedSeasons, setCollapsedSeasons] = useState({});
  const cats = ['all', 'war', 'mass', 'revolt', 'gov'];
  const countByCat = cat => CHAPTERS_INDEX.filter(c => cat === 'all' || c.cat === cat).length;
  const visibleChapters = activeCat === 'all' ? CHAPTERS_INDEX : CHAPTERS_INDEX.filter(c => c.cat === activeCat);
  const season1 = visibleChapters.filter(c => c.season === 1);
  const season2 = visibleChapters.filter(c => c.season === 2);
  const chapterPreviewSrc = chapterNumber => {
    // chapter 1 corresponds to page 5
    return getPageImageUrl(chapterNumber + 4, 800);
  };
  const toggleSeason = seasonNum => {
    setCollapsedSeasons(prev => ({
      ...prev,
      [seasonNum]: !prev[seasonNum]
    }));
  };
  const renderCard = ch => {
    if (ch.img) {
      return <div key={ch.n} className={`ch-card has-img${activeCat !== 'all' && activeCat !== ch.cat ? ' dimmed' : ''}`} data-cat={ch.cat}>
          <div className="ch-img-wrap">
            <img src={ch.img} alt={ch.title} loading="lazy" />
          </div>
          <div className="ch-body">
            <div className="ch-meta-row">
              <span className="ch-num">{ch.n}</span>
              <span className="ch-cat">
                <span className="swatch"></span>
                {CAT_LABEL[ch.cat]}
              </span>
            </div>
            <div className="ch-title">{ch.title}</div>
            <div className="ch-sub">{ch.sub}</div>
            <div className="ch-date">{ch.date}</div>
          </div>
        </div>;
    }
    return <div key={ch.n} className={`ch-card previewable${activeCat !== 'all' && activeCat !== ch.cat ? ' dimmed' : ''}`} data-cat={ch.cat}>
        <div className="ch-body-preview">
          <div className="ch-meta-row">
            <span className="ch-num">{ch.n}</span>
            <span className="ch-cat">
              <span className="swatch"></span>
              {CAT_LABEL[ch.cat]}
            </span>
          </div>
          <div className="ch-title">{ch.title}</div>
          <div className="ch-sub">{ch.sub}</div>
          <div className="ch-date">{ch.date}</div>
        </div>
        <div className="ch-preview">
          <img src={chapterPreviewSrc(ch.n)} alt="" loading="lazy" aria-hidden="true" />
        </div>
      </div>;
  };
  return <section className="inside" id="inside">
      <div className="wrap">
        <div className="inside-head">
          <div>
            <div className="eyebrow">Chapter index</div>
            <h2>All <em>24</em><br />chapters</h2>
          </div>
          <p>
            Browse every chapter across both seasons. Filter by thread — conquest, governance, mass politics, or revolutionary resistance.
          </p>
        </div>

        <div className="cat-pills">
          {cats.map(cat => <button key={cat} className={`cat-pill${activeCat === cat ? ' on' : ''}`} data-cat={cat !== 'all' ? cat : undefined} onClick={() => setActiveCat(cat)} data-tracking-id={`btn-0aa3ff171ece8253_${cat}`}>
              {cat !== 'all' && <span className="swatch"></span>}
              {cat === 'all' ? 'All chapters' : CAT_LABEL[cat]}
              <span className="ct">{countByCat(cat)}</span>
            </button>)}
        </div>

        {season1.length > 0 && <>
            <div className="season-rule">
              <span className="lbl">Season <em>I</em></span>
              <span className="line"></span>
              <span className="span">1600 — 1900</span>
            </div>
            <button className="season-toggle" onClick={() => toggleSeason(1)} data-tracking-id="btn-2e3d528491f46996">
              {collapsedSeasons[1] ? 'Show' : 'Hide'} Season I <span className="arrow">→</span>
            </button>
            {!collapsedSeasons[1] && <div className="ch-grid">
                {season1.map(renderCard)}
              </div>}
          </>}

        {season2.length > 0 && <>
            <div className="season-rule">
              <span className="lbl">Season <em>II</em></span>
              <span className="line"></span>
              <span className="span">1870 — 1947</span>
            </div>
            <button className="season-toggle" onClick={() => toggleSeason(2)} data-tracking-id="btn-ab66fed97236eb2d">
              {collapsedSeasons[2] ? 'Show' : 'Hide'} Season II <span className="arrow">→</span>
            </button>
            {!collapsedSeasons[2] && <div className="ch-grid">
                {season2.map(renderCard)}
              </div>}
          </>}

        <div className="inside-foot">
          <span><b>24 chapters</b> · 2 seasons · full index</span>
          <span className="scroll-hint">Scroll to browse</span>
        </div>
      </div>
    </section>;
}
function AuthorLine() {
  return <section className="author-line">
      <div className="wrap">
        <div className="al-grid">
          <div className="al-portrait">
            <img src="https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2RBkUqh3LVjYlDQLDGXc6cEkFC7/author-portrait-8sp5tjo44f5.jpg" alt="Bhanu Sharma" />
          </div>
          <div className="al-copy">
            <span className="al-eyebrow">A note from the author</span>
            <p className="al-quote">
              "I wanted a book that would give readers the <b>shape</b> of the story — before they went deeper."
            </p>
            <div className="al-attr">
              <b>Bhanu Sharma</b>
              <span className="sep">·</span>
              <span>Author, Empire &amp; Freedom</span>
              <span className="sep">·</span>
              <span>2026</span>
            </div>
            <p className="al-note">
              Empire &amp; Freedom grew out of a frustration: most histories of colonial India are either too dense for a first read, or too thin to be useful. This book tries to be the bridge — a visual, structured entry point for anyone who wants to understand what happened, and why it still matters.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function Readers() {
  return <section className="readers" id="readers">
      <div className="wrap">
        <div className="readers-head">
          <h2>Who reads <em>this</em></h2>
          <span className="meta">3 reader types</span>
        </div>
        <div className="rgrid">
          {READERS.map((r, i) => <div className="rcard" key={i}>
              <div className="rno">0{i + 1}</div>
              <p className="rq">{r.q}</p>
              <div className="rattr">
                <span className="name">{r.name}</span>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function Obtain() {
  return <section className="obtain" id="obtain">
      <div className="wrap">
        <div className="obtain-head">
          <span className="eyebrow">Get the book</span>
          <h2>Buy <em>Empire<br />&amp; Freedom</em></h2>
          <p>
            Available now on Kindle worldwide. Read on any device — phone, tablet, or e-reader. Instant delivery.
          </p>
        </div>

        <div className="decide decide-single">
          <div className="choice choice-solo">
            <div className="choice-head">
              <h3>Kindle <em>Ebook</em></h3>
              <span className="choice-tag">Digital · Instant</span>
            </div>
            <p className="sub">
              31 visual pages, 24 chapters, full colour illustrations. Available on all Kindle apps and devices.
            </p>
            <div className="price">
              <span className="cur">$</span>
              <span className="big">19.47</span>
              <span className="usd">USD · Kindle</span>
            </div>
            <ul>
              <li>24 illustrated chapters across 2 seasons</li>
              <li>31 full-colour visual pages</li>
              <li>Maps, timelines &amp; visual notes</li>
              <li>Read on any device with the free Kindle app</li>
            </ul>

            <div className="market-grid">
              <div className="market-card">
                <div className="market-label">United States</div>
                <div className="market-price">$19.47</div>
                <div className="market-meta">Amazon.com · Kindle edition</div>
                <div className="market-links">
                  <a className="store-link" href={STORE_LINKS.us} target="_blank" rel="noopener noreferrer" data-tracking-id="link-b912530d080f2d2e">Amazon US</a>
                </div>
              </div>
              <div className="market-card">
                <div className="market-label">India</div>
                <div className="market-price">₹815</div>
                <div className="market-meta">Amazon.in · Kindle edition</div>
                <div className="market-links">
                  <a className="store-link" href={STORE_LINKS.india} target="_blank" rel="noopener noreferrer" data-tracking-id="link-997e1b79111a84b4">Amazon IN</a>
                </div>
              </div>
              <div className="market-card">
                <div className="market-label">United Kingdom</div>
                <div className="market-price">£19.47</div>
                <div className="market-meta">Amazon.co.uk · Kindle edition</div>
                <div className="market-links">
                  <a className="store-link" href={STORE_LINKS.uk} target="_blank" rel="noopener noreferrer" data-tracking-id="link-81f741a0fb57dcdf">Amazon UK</a>
                </div>
              </div>
              <div className="market-card">
                <div className="market-label">Europe</div>
                <div className="market-price">€19.47</div>
                <div className="market-meta">Amazon DE / FR / ES / IT / NL</div>
                <div className="market-links">
                  <a className="store-link" href={STORE_LINKS.de} target="_blank" rel="noopener noreferrer" data-tracking-id="link-feb3e8d89ee4d315">DE</a>
                  <a className="store-link" href={STORE_LINKS.fr} target="_blank" rel="noopener noreferrer" data-tracking-id="link-cedd32daba2258f9">FR</a>
                  <a className="store-link" href={STORE_LINKS.es} target="_blank" rel="noopener noreferrer" data-tracking-id="link-4986a2a307ffbd72">ES</a>
                  <a className="store-link" href={STORE_LINKS.it} target="_blank" rel="noopener noreferrer" data-tracking-id="link-0d0063a12e562593">IT</a>
                  <a className="store-link" href={STORE_LINKS.nl} target="_blank" rel="noopener noreferrer" data-tracking-id="link-b37d36b685550c23">NL</a>
                </div>
              </div>
            </div>

            <a href={STORE_LINKS.us} target="_blank" rel="noopener noreferrer" className="btn btn-ox btn-lg cta" data-tracking-id="link-389c732cd9b03d52">
              Buy on Amazon <span className="arrow">→</span>
            </a>
          </div>
        </div>

        <div className="obtain-foot">
          <span>Available in <b>8 markets</b> on Kindle</span>
          <div className="guarantees">
            <span>Instant delivery</span>
            <span>Any device</span>
            <span>Full colour</span>
          </div>
        </div>
      </div>
    </section>;
}
function MobileBuyBar() {
  return null;
  return <div className="mobile-buy-bar">
      <div className="copy">
        <span className="eyebrow">Kindle ebook</span>
        <div className="line">Get it for <em>$19.47</em></div>
      </div>
      <a href={STORE_LINKS.us} target="_blank" rel="noopener noreferrer" className="btn btn-ox" data-tracking-id="link-98bdf94530e6e23f">
        Buy now <span className="arrow">→</span>
      </a>
    </div>;
}
function Footer() {
  return <footer>
      <div className="wrap foot-inner">
        <div className="foot-top">
          <div className="foot-brand">
            <a href="#" className="brand" data-tracking-id="link-cd7327982f8f7568">Empire <em>&amp;</em> Freedom</a>
            <p>A visual history of the East India Company, the British Raj, and Indian independence — 1600 to 1947.</p>
          </div>
          <div>
            <h4>Navigate</h4>
            <a href="#seasons" data-tracking-id="link-f228eb79e7460068">Seasons</a>
            <a href="#object" data-tracking-id="link-5beada5c07c6f15a">The Book</a>
            <a href="#inside" data-tracking-id="link-58dc5926b3250b92">Chapters</a>
            <a href="#readers" data-tracking-id="link-c2604e57607043f4">Readers</a>
            <a href="#obtain" data-tracking-id="link-03f8df4d6d383b45">Buy</a>
          </div>
        </div>
        <div className="foot-bottom">
          <span>&copy; 2026 Bhanu Sharma · All rights reserved</span>
          <span>Empire &amp; Freedom · Kindle Ebook</span>
        </div>
      </div>
    </footer>;
}

// ─── App ─────────────────────────────────────────────────────────────────────

function App() {
  useEffect(() => {
    if (!document.getElementById('ef2-fonts')) {
      const link = document.createElement('link');
      link.id = 'ef2-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500;1,600;1,700&family=Inter+Tight:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
      document.head.appendChild(link);
    }
    if (!document.getElementById('ef2-styles')) {
      const style = document.createElement('style');
      style.id = 'ef2-styles';
      style.textContent = scopeCSS(EF2_CSS, '.ef2-root');
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById('ef2-styles');
      if (el) el.remove();
    };
  }, []);
  return <div className="ef2-root">
      <div className="ef2-grain" aria-hidden="true"></div>
      <Nav />
      <Hero />
      <TickerBar />
      <Seasons />
      <TheObject />
      <Inside />
      <AuthorLine />
      <Readers />
      <Obtain />
      <Footer />
      <MobileBuyBar />
    </div>;
}
export default App;